// manufacturing/RawMaterialDispatch.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip, } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Select from 'react-select';
import {
    Plus, Search, RotateCcw, Eye, Trash2, ChevronUp,
    Truck, Calendar, Package, AlertCircle, XCircle, CheckCircle
} from 'feather-icons-react/build/IconComponents';
import { manufacturingActions } from "../../core/action/manufacturingAction";
import { unitAPI } from "../../core/action/api";

const MySwal = withReactContent(Swal);

const RawMaterialDispatch = () => {
    const dispatchRedux = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State management
    const [dispatches, setDispatches] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [updatingStatus, setUpdatingStatus] = useState(null);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedDispatch, setSelectedDispatch] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        dispatch_date: new Date().toISOString().split('T')[0],
        from_location: 'Head Office',
        to_location: '',
        reference_no: '',
        notes: '',
        items: []
    });

    const [errors, setErrors] = useState({});

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        from_location: '',
        to_location: '',
        from_date: '',
        to_date: ''
    });

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Location options
    const locationOptions = manufacturingActions.getLocationOptions();

    // Status options
    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'dispatched', label: 'Dispatched' },
        { value: 'received', label: 'Received' },
        { value: 'partially_received', label: 'Partially Received' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Check permissions
    const canCreate = manufacturingActions.hasManufacturingPermission('create-dispatch');
    const canView = manufacturingActions.hasManufacturingPermission('view-dispatch');
    const canDelete = manufacturingActions.hasManufacturingPermission('delete-dispatch');
    const canUpdateStatus = manufacturingActions.hasManufacturingPermission('update-dispatch-status');

    // Load units
    const loadUnits = useCallback(async () => {
        try {
            const response = await unitAPI.list({ limit: 1000, status: 'Active' });
            if (response?.status && response?.data?.units) {
                setUnits(response.data.units);
            } else if (response?.status && Array.isArray(response?.data)) {
                setUnits(response.data);
            }
        } catch (error) {
            console.error('Error loading units:', error);
        }
    }, []);

    // Load raw materials
    const loadRawMaterials = useCallback(async () => {
        try {
            const response = await manufacturingActions.getRawMaterials({
                limit: 1000,
                status: 'active',
                has_stock: true
            });

            if (response.success) {
                setRawMaterials(response.data || []);
                if (response.data.length === 0) {
                    toast.info('No raw materials available in stock. Please purchase raw materials first.');
                }
            } else {
                setRawMaterials([]);
                toast.error(response.message || 'Failed to load raw materials');
            }
        } catch (error) {
            console.error('Error loading raw materials:', error);
            setRawMaterials([]);
            toast.error('Failed to load raw materials');
        }
    }, []);

    // Load dispatches
    const loadDispatches = useCallback(async (page = pagination.page) => {
        if (!canView) {
            toast.error('You do not have permission to view dispatches');
            return;
        }

        try {
            setLoading(true);
            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                status: filters.status,
                from_location: filters.from_location,
                to_location: filters.to_location,
                from_date: filters.from_date,
                to_date: filters.to_date
            };

            const result = await manufacturingActions.getDispatches(params);

            if (result.success) {
                setDispatches(result.data || []);
                setPagination(prev => ({
                    ...prev,
                    page: result.page || page,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                }));
            } else {
                toast.error(result.message || 'Failed to load dispatches');
            }
        } catch (error) {
            console.error('Error loading dispatches:', error);
            toast.error('Failed to load dispatches');
        } finally {
            setLoading(false);
        }
    }, [canView, filters, pagination.limit, pagination.page]);

    // Initial load
    useEffect(() => {
        loadUnits();
        loadRawMaterials();
        loadDispatches(1);
    }, [loadUnits, loadRawMaterials, loadDispatches]);

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        loadDispatches(1);
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Handle select change
    const handleSelectChange = (selected, name) => {
        setFormData(prev => ({ ...prev, [name]: selected?.value || '' }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Add raw material to dispatch
    const handleAddMaterial = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                id: Date.now(),
                material_id: '',
                product_name: '',
                sku: '',
                quantity: 0,
                unit: 'PCS',
                unit_price: 0,
                total_price: 0,
                max_quantity: 0,
                remarks: ''
            }]
        }));
    };

    // Remove material from dispatch
    const handleRemoveMaterial = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    // Handle raw material selection
    const handleMaterialSelect = (index, selectedOption) => {
        if (!selectedOption) return;

        const material = selectedOption.data;

        if (material) {
            const availableStock = parseFloat(material.quantity) || 0;

            if (availableStock <= 0) {
                toast.error(`Insufficient stock for ${material.material_name}. Available: ${availableStock}`);
                return;
            }

            const updatedItems = [...formData.items];
            updatedItems[index] = {
                ...updatedItems[index],
                material_id: material.id,
                product_name: material.material_name,
                sku: material.sku,
                quantity: 0,
                unit: material.unit_name || 'PCS',
                unit_price: parseFloat(material.cost_price) || 0,
                total_price: 0,
                max_quantity: availableStock,
                remarks: ''
            };
            setFormData(prev => ({ ...prev, items: updatedItems }));
        }
    };

    // Handle quantity change
    const handleQuantityChange = (index, quantity) => {
        const updatedItems = [...formData.items];
        const qty = parseFloat(quantity) || 0;
        const maxQty = updatedItems[index].max_quantity || 0;

        if (qty > maxQty) {
            toast.warning(`Cannot dispatch more than available stock (${maxQty})`);
            updatedItems[index].quantity = maxQty;
        } else {
            updatedItems[index].quantity = qty;
        }

        updatedItems[index].total_price = (updatedItems[index].unit_price || 0) * updatedItems[index].quantity;
        setFormData(prev => ({ ...prev, items: updatedItems }));

        if (errors[`quantity_${index}`]) {
            setErrors(prev => ({ ...prev, [`quantity_${index}`]: '' }));
        }
    };

    // Handle unit price change
    const handleUnitPriceChange = (index, price) => {
        const updatedItems = [...formData.items];
        const unitPrice = parseFloat(price) || 0;
        updatedItems[index].unit_price = unitPrice;
        updatedItems[index].total_price = unitPrice * updatedItems[index].quantity;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.to_location) {
            newErrors.to_location = 'Destination location is required';
        }

        if (formData.items.length === 0) {
            newErrors.items = 'At least one raw material is required';
        }

        for (let i = 0; i < formData.items.length; i++) {
            const item = formData.items[i];
            if (!item.material_id) {
                newErrors[`material_${i}`] = 'Raw material selection is required';
            }
            if (item.quantity <= 0) {
                newErrors[`quantity_${i}`] = 'Quantity must be greater than 0';
            }
            if (item.quantity > item.max_quantity) {
                newErrors[`stock_${i}`] = `Insufficient stock. Available: ${item.max_quantity}`;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        const validItems = formData.items.filter(item => item.material_id && item.quantity > 0);
        if (validItems.length === 0) {
            toast.error('No valid items to dispatch');
            return;
        }

        setCreating(true);

        const dataToSend = {
            dispatch_date: formData.dispatch_date,
            from_location: formData.from_location,
            to_location: formData.to_location,
            reference_no: formData.reference_no || null,
            notes: formData.notes || null,
            items: validItems.map(item => ({
                material_id: item.material_id,
                product_name: item.product_name || '',
                sku: item.sku || '',
                quantity: parseFloat(item.quantity),
                unit: item.unit,
                unit_price: parseFloat(item.unit_price) || 0,
                total_price: parseFloat(item.total_price) || 0,
                remarks: item.remarks || null
            }))
        };

        try {
            const result = await manufacturingActions.createDispatch(dataToSend);

            if (result.success) {
                toast.success(result.message || 'Raw material dispatched successfully');
                resetForm();
                setShowCreateModal(false);
                loadDispatches(1);
                loadRawMaterials();

                MySwal.fire({
                    icon: 'success',
                    title: 'Dispatched!',
                    text: result.message || 'Raw material dispatched successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                toast.error(result.message || 'Failed to create dispatch');
                setErrors({ form: result.message });
            }
        } catch (error) {
            console.error('Error creating dispatch:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to create dispatch';
            toast.error(errorMsg);
            setErrors({ form: errorMsg });
        } finally {
            setCreating(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            dispatch_date: new Date().toISOString().split('T')[0],
            from_location: 'Head Office',
            to_location: '',
            reference_no: '',
            notes: '',
            items: []
        });
        setErrors({});
    };

    // View dispatch details
    const handleViewDispatch = async (dispatch) => {
        try {
            const result = await manufacturingActions.getDispatch(dispatch.id);
            if (result.success) {
                setSelectedDispatch(result.data);
                setShowViewModal(true);
            } else {
                toast.error(result.message || 'Failed to load dispatch details');
            }
        } catch (error) {
            console.error('Error loading dispatch details:', error);
            toast.error('Failed to load dispatch details');
        }
    };

    // Handle delete dispatch
    const handleDeleteDispatch = async (dispatch) => {
        if (!canDelete) {
            toast.error('You do not have permission to delete dispatches');
            return;
        }

        const result = await MySwal.fire({
            title: 'Delete Dispatch?',
            html: `<p>Are you sure you want to delete dispatch <strong>${dispatch.dispatch_no}</strong>?</p>
                   <p class="text-danger">This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                // Note: Implement delete API endpoint as needed
                toast.success('Dispatch deleted successfully');
                loadDispatches();
            } catch (error) {
                console.error('Error deleting dispatch:', error);
                toast.error('Failed to delete dispatch');
            }
        }
    };

    // Handle update status - using SweetAlert for better UX
    const handleUpdateStatus = async (dispatch, newStatus) => {
        if (!canUpdateStatus) {
            toast.error('You do not have permission to update dispatch status');
            return;
        }

        if (updatingStatus === dispatch.id) {
            return; // Already updating
        }

        let statusText = '';
        let statusColor = '';
        let statusIcon = '';

        if (newStatus === 'dispatched') {
            statusText = 'Dispatched';
            statusColor = '#28a745';
            statusIcon = '🚚';
        } else if (newStatus === 'cancelled') {
            statusText = 'Cancelled';
            statusColor = '#dc3545';
            statusIcon = '❌';
        }

        const result = await MySwal.fire({
            title: `Mark as ${statusText}?`,
            html: `
                <div style="text-align: left;">
                    <p><strong>Dispatch No:</strong> ${dispatch.dispatch_no}</p>
                    <p><strong>Current Status:</strong> ${dispatch.status?.toUpperCase() || 'PENDING'}</p>
                    <hr>
                    <p class="text-${newStatus === 'dispatched' ? 'success' : 'danger'}">
                        ${statusIcon} Are you sure you want to mark this dispatch as <strong>${statusText.toUpperCase()}</strong>?
                    </p>
                    ${newStatus === 'dispatched' ?
                    '<p class="text-info small">⚠️ Once marked as Dispatched, the materials will be available for receiving at the manufacturing unit.</p>' :
                    '<p class="text-warning small">⚠️ Cancelled dispatches cannot be received and stock will remain at Head Office.</p>'
                }
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: statusColor,
            cancelButtonColor: '#6c757d',
            confirmButtonText: `Yes, mark as ${statusText}`,
            cancelButtonText: 'No, keep as is'
        });

        if (result.isConfirmed) {
            setUpdatingStatus(dispatch.id);
            try {
                const updateResult = await manufacturingActions.updateDispatchStatus(dispatch.id, newStatus);
                if (updateResult.success) {
                    toast.success(`Dispatch marked as ${statusText} successfully`);
                    loadDispatches(pagination.page);
                } else {
                    toast.error(updateResult.message || 'Failed to update status');
                }
            } catch (error) {
                console.error('Error updating status:', error);
                toast.error('Failed to update dispatch status');
            } finally {
                setUpdatingStatus(null);
            }
        }
    };

    // Raw material options for dropdown
    const rawMaterialOptions = (Array.isArray(rawMaterials) ? rawMaterials : []).map(material => ({
        value: material.id,
        label: `${material.material_name} (${material.sku}) - Stock: ${material.quantity || 0} ${material.unit_name || 'PCS'}`,
        isDisabled: (material.quantity || 0) <= 0,
        data: material
    }));

    // Tooltip renderer
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Get status badge class
    const getStatusBadge = (status) => {
        const badgeClass = manufacturingActions.getStatusBadgeClass(status);
        let displayStatus = status?.toUpperCase() || 'N/A';
        if (displayStatus === 'PARTIALLY_RECEIVED') {
            displayStatus = 'PARTIAL';
        }
        return <span className={`badge ${badgeClass}`}>{displayStatus}</span>;
    };

    // Calculate totals
    const calculateTotals = () => {
        const totalItems = formData.items.length;
        const totalQuantity = formData.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const totalValue = formData.items.reduce((sum, item) => sum + (item.total_price || 0), 0);
        return { totalItems, totalQuantity, totalValue };
    };

    const totals = calculateTotals();
    console.log('Calculated unit:', units);

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Raw Material Dispatch</h4>
                        <h6>Dispatch raw materials to manufacturing units</h6>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={() => { loadDispatches(1); loadRawMaterials(); }}>
                                    <RotateCcw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatchRedux(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>

                    {canCreate && (
                        <div className="page-btn">
                            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                <Plus size={18} className="me-2" />
                                New Dispatch
                            </button>
                        </div>
                    )}
                </div>

                {/* Statistics Cards */}
                <div className="row">
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                                        <Truck size={24} className="text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Dispatches</p>
                                        <h3 className="mb-0">{pagination.total}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-warning bg-opacity-10 p-3 rounded me-3">
                                        <AlertCircle size={24} className="text-warning" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Pending</p>
                                        <h3 className="mb-0">
                                            {dispatches.filter(d => d.status === 'pending').length}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                                        <CheckCircle size={24} className="text-success" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Dispatched</p>
                                        <h3 className="mb-0">
                                            {dispatches.filter(d => d.status === 'dispatched').length}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-info bg-opacity-10 p-3 rounded me-3">
                                        <Package size={24} className="text-info" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Items</p>
                                        <h3 className="mb-0">
                                            {dispatches.reduce((sum, d) => sum + (d.total_items || 0), 0)}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>Search</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Dispatch no, reference..."
                                            value={filters.search}
                                            onChange={(e) => handleFilterChange('search', e.target.value)}
                                        />
                                        <button className="btn btn-primary" onClick={() => loadDispatches(1)}>
                                            <Search size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>Status</label>
                                    <Select
                                        options={statusOptions}
                                        isClearable
                                        placeholder="All Status"
                                        onChange={(selected) => handleFilterChange('status', selected?.value || '')}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>From Location</label>
                                    <Select
                                        options={locationOptions}
                                        isClearable
                                        placeholder="All Locations"
                                        onChange={(selected) => handleFilterChange('from_location', selected?.value || '')}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>To Location</label>
                                    <Select
                                        options={locationOptions}
                                        isClearable
                                        placeholder="All Locations"
                                        onChange={(selected) => handleFilterChange('to_location', selected?.value || '')}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.from_date}
                                        onChange={(e) => handleFilterChange('from_date', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.to_date}
                                        onChange={(e) => handleFilterChange('to_date', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Dispatches Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : dispatches.length === 0 ? (
                            <div className="text-center py-5">
                                <Truck size={48} className="text-muted mb-3" />
                                <h5>No dispatches found</h5>
                                <p className="text-muted">Create your first dispatch to get started</p>
                                {canCreate && (
                                    <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                        <Plus size={16} className="me-2" />
                                        Create Dispatch
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th width="40px">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedRows.length === dispatches.length && dispatches.length > 0}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedRows(dispatches.map(d => d.id));
                                                        } else {
                                                            setSelectedRows([]);
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th>Dispatch No</th>
                                            <th>Date</th>
                                            <th>From</th>
                                            <th>To</th>
                                            <th>Items</th>
                                            <th>Total Qty</th>
                                            <th>Status</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dispatches.map((dispatch) => (
                                            <tr key={dispatch.id}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={selectedRows.includes(dispatch.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedRows([...selectedRows, dispatch.id]);
                                                            } else {
                                                                setSelectedRows(selectedRows.filter(id => id !== dispatch.id));
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <strong>{dispatch.dispatch_no}</strong>
                                                    {dispatch.reference_no && (
                                                        <div className="small text-muted">Ref: {dispatch.reference_no}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Calendar size={14} className="me-1 text-muted" />
                                                        {formatDate(dispatch.dispatch_date)}
                                                    </div>
                                                </td>
                                                <td>{dispatch.from_location}</td>
                                                <td>{dispatch.to_location}</td>
                                                <td>{dispatch.total_items || 0}</td>
                                                <td>{dispatch.total_quantity || 0}</td>
                                                <td>{getStatusBadge(dispatch.status)}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-info me-1"
                                                        onClick={() => handleViewDispatch(dispatch)}
                                                        title="View Details"
                                                    >
                                                        <Eye size={14} />
                                                    </button>

                                                    {/* Status update button - using simple button with onClick instead of dropdown */}
                                                    {canUpdateStatus && dispatch.status === 'pending' && (
                                                        <button
                                                            className="btn btn-sm btn-success me-1"
                                                            onClick={() => handleUpdateStatus(dispatch, 'dispatched')}
                                                            title="Mark as Dispatched"
                                                            disabled={updatingStatus === dispatch.id}
                                                        >
                                                            {updatingStatus === dispatch.id ? (
                                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                            ) : (
                                                                <Truck size={14} />
                                                            )}
                                                        </button>
                                                    )}

                                                    {/* Cancel button for pending dispatches */}
                                                    {canUpdateStatus && dispatch.status === 'pending' && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger me-1"
                                                            onClick={() => handleUpdateStatus(dispatch, 'cancelled')}
                                                            title="Cancel Dispatch"
                                                            disabled={updatingStatus === dispatch.id}
                                                        >
                                                            <XCircle size={14} />
                                                        </button>
                                                    )}

                                                    {canDelete && dispatch.status === 'pending' && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDeleteDispatch(dispatch)}
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="text-muted">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                </div>
                                <nav>
                                    <ul className="pagination">
                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadDispatches(pagination.page - 1)}>
                                                Previous
                                            </button>
                                        </li>
                                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                            let pageNum = pagination.page;
                                            if (pagination.totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (pagination.page <= 3) {
                                                pageNum = i + 1;
                                            } else if (pagination.page >= pagination.totalPages - 2) {
                                                pageNum = pagination.totalPages - 4 + i;
                                            } else {
                                                pageNum = pagination.page - 2 + i;
                                            }
                                            return (
                                                <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                    <button className="page-link" onClick={() => loadDispatches(pageNum)}>
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadDispatches(pagination.page + 1)}>
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Create Dispatch Modal - same as before */}
            {showCreateModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <Truck size={18} className="me-2" />
                                    New Raw Material Dispatch
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)} />
                            </div>
                            <div className="modal-body">
                                {/* ... keep the existing modal content ... */}
                                <div className="row mb-4">
                                    <div className="col-md-4">
                                        <label className="form-label">Dispatch Date *</label>
                                        <input
                                            type="date"
                                            name="dispatch_date"
                                            className="form-control"
                                            value={formData.dispatch_date}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">From Location *</label>
                                        <input
                                            type="text"
                                            name="from_location"
                                            className="form-control"
                                            value={formData.from_location}
                                            readOnly
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">To Location *</label>
                                        <Select
                                            options={locationOptions}
                                            value={locationOptions.find(opt => opt.value === formData.to_location)}
                                            onChange={(selected) => handleSelectChange(selected, 'to_location')}
                                            placeholder="Select destination"
                                            className={errors.to_location ? 'is-invalid' : ''}
                                        />
                                        {errors.to_location && <div className="text-danger small mt-1">{errors.to_location}</div>}
                                    </div>
                                    <div className="col-md-6 mt-3">
                                        <label className="form-label">Reference No</label>
                                        <input
                                            type="text"
                                            name="reference_no"
                                            className="form-control"
                                            value={formData.reference_no}
                                            onChange={handleInputChange}
                                            placeholder="Optional reference number"
                                        />
                                    </div>
                                    <div className="col-md-6 mt-3">
                                        <label className="form-label">Notes</label>
                                        <textarea
                                            name="notes"
                                            className="form-control"
                                            value={formData.notes}
                                            onChange={handleInputChange}
                                            rows="2"
                                            placeholder="Additional notes"
                                        />
                                    </div>
                                </div>

                                <div className="border-top pt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-bold">
                                            <Package size={16} className="me-2" />
                                            Raw Materials to Dispatch
                                        </h6>
                                        <button type="button" className="btn btn-sm btn-primary" onClick={handleAddMaterial}>
                                            <Plus size={14} className="me-1" /> Add Raw Material
                                        </button>
                                    </div>

                                    {errors.form && <div className="alert alert-danger">{errors.form}</div>}
                                    {errors.items && <div className="alert alert-danger">{errors.items}</div>}

                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '35%' }}>Raw Material</th>
                                                    <th style={{ width: '15%' }}>Quantity</th>
                                                    <th style={{ width: '10%' }}>Unit</th>
                                                    <th style={{ width: '20%' }}>Unit Price (₹)</th>
                                                    <th style={{ width: '15%' }}>Total (₹)</th>
                                                    <th style={{ width: '5%' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.items.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4 text-muted">
                                                            Click Add Raw Material to add items to dispatch
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    formData.items.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <Select
                                                                    options={rawMaterialOptions}
                                                                    onChange={(selected) => handleMaterialSelect(index, selected)}
                                                                    placeholder="Select raw material"
                                                                    className={errors[`material_${index}`] ? 'is-invalid' : ''}
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                                />
                                                                {errors[`material_${index}`] && <div className="text-danger small mt-1">{errors[`material_${index}`]}</div>}
                                                                {errors[`stock_${index}`] && <div className="text-danger small mt-1">{errors[`stock_${index}`]}</div>}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control ${errors[`quantity_${index}`] ? 'is-invalid' : ''}`}
                                                                    value={item.quantity || ''}
                                                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                                    step="1"
                                                                    min="0"
                                                                    max={item.max_quantity}
                                                                    placeholder="Qty"
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={item.unit}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    value={item.unit_price || ''}
                                                                    onChange={(e) => handleUnitPriceChange(index, e.target.value)}

                                                                    min="0"
                                                                    placeholder="Price"
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control bg-light"
                                                                    value={item.total_price.toFixed(2)}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() => handleRemoveMaterial(index)}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                            {formData.items.length > 0 && (
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="2" className="text-end fw-bold">Totals:</td>
                                                        <td className="fw-bold">{totals.totalItems} Items</td>
                                                        <td className="fw-bold">{totals.totalQuantity} Units</td>
                                                        <td className="fw-bold">₹{totals.totalValue.toFixed(2)}</td>
                                                        <td />
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                    disabled={creating}
                                >
                                    {creating ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <Truck size={16} className="me-2" />
                                            Create Dispatch
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Dispatch Modal - same as before */}
            {showViewModal && selectedDispatch && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <Truck size={18} className="me-2" />
                                    Dispatch Details - {selectedDispatch.dispatch_no}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="row mb-4">
                                    <div className="col-md-6">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">Dispatch Information</h6>
                                                <p><strong>Dispatch No:</strong> {selectedDispatch.dispatch_no}</p>
                                                <p><strong>Date:</strong> {formatDate(selectedDispatch.dispatch_date)}</p>
                                                <p><strong>From:</strong> {selectedDispatch.from_location}</p>
                                                <p><strong>To:</strong> {selectedDispatch.to_location}</p>
                                                <p><strong>Status:</strong> {getStatusBadge(selectedDispatch.status)}</p>
                                                {selectedDispatch.reference_no && (
                                                    <p><strong>Reference No:</strong> {selectedDispatch.reference_no}</p>
                                                )}
                                                {selectedDispatch.notes && (
                                                    <p><strong>Notes:</strong> {selectedDispatch.notes}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">Summary</h6>
                                                <p><strong>Total Items:</strong> {selectedDispatch.total_items}</p>
                                                <p><strong>Total Quantity:</strong> {selectedDispatch.total_quantity}</p>
                                                <p><strong>Created At:</strong> {formatDate(selectedDispatch.created_at)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <h6 className="fw-bold mb-3">Dispatched Raw Materials</h6>
                                <div className="table-responsive">
                                    <table className="table table-bordered">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Raw Material</th>
                                                <th>SKU</th>
                                                <th>Quantity</th>
                                                <th>Unit</th>
                                                <th>Unit Price (₹)</th>
                                                <th>Total (₹)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedDispatch.items && selectedDispatch.items.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>{item.product_name}</td>
                                                    <td><code>{item.sku}</code></td>
                                                    <td>{item.quantity}</td>
                                                    <td>{item.unit}</td>
                                                    <td>₹{parseFloat(item.unit_price).toFixed(2)}</td>
                                                    <td>₹{parseFloat(item.total_price).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan="5" className="text-end fw-bold">Grand Total:</td>
                                                <td className="fw-bold">
                                                    ₹{selectedDispatch.items?.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0).toFixed(2)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RawMaterialDispatch;